import React, {useState} from 'react';

import {Container, Row} from 'react-bootstrap';
import {Button} from '@/components/ui';
import {BaseModal} from 'components/modals';
import {Tooltip} from '@/components/tooltips';
import Col from 'react-bootstrap/Col';
import {TextSamplesModalContentsWrapper} from '../textsamples/modals/TextSamplesModalContentsWrapper';
import CellLoader from '@/components/loading/CellLoader';
import useOptimizedConfig from '@/hooks/useOptimizedConfig';
import {ConcordanceData, EventTextField, EventTextFieldOption} from 'entities/event';
import {ChartFieldToQueryOption} from 'entities/chart';
import i18n from 'i18n';
import {useFormattedDateTime} from '@/components/utils/FormattedDateTime';
import {buildEventDetailURL} from '@/utils/urlBuilder';
import {hasPermission} from '@/utils/permissions/permissionDebug';

interface Props {
    data: ConcordanceData[];
    fieldToQuery: ChartFieldToQueryOption;
    previewOnly: boolean;
    queryDuration?: string;
    chartSettingsView?: boolean;
}

const ConcordanceWrapper = React.memo<Props>(
    ({data, fieldToQuery, previewOnly, queryDuration = '0', chartSettingsView = false}) => {
        const {formatDateTime, isLoaded: configLoaded} = useFormattedDateTime();
        const {userData, loading: loadingUserData} = useOptimizedConfig();

        const [modalShown, setModalShown] = useState<boolean>(false);
        const [activeEventId, setActiveEventId] = useState<number | null>(null);
        const [activeEvent, setActiveEvent] = useState<ConcordanceData | null>(null);


        const openDetailModal = (x: ConcordanceData) => {
            if (!previewOnly) {
                setActiveEventId(x.event_id);
                setActiveEvent(x);
                setModalShown(true);
            }
        };

        let fieldName: EventTextField;
        if (fieldToQuery === EventTextFieldOption.AllFields) {
            fieldName = EventTextFieldOption.EventText;
        } else {
            fieldName = fieldToQuery;
        }

        let modalContents;
        if (modalShown && !previewOnly && activeEvent) {
            modalContents = (
                <TextSamplesModalContentsWrapper
                    eventData={activeEvent}
                    fieldToShow={fieldName}
                    autoScrollScope="textSamples"
                />
            );
        }

        let previewClass = '';
        if (previewOnly) {
            previewClass = 'preview-only';
        }

        const userCanAccessEventDetailView = hasPermission('fs.event.list', userData?.permissions, true);

        if (!loadingUserData) {
            return (
                <>
                    <div
                        className={`w-100 text-center concordance-wrapper ${
                            chartSettingsView
                                ? 'concordance-wrapper--chart-settings'
                                : 'chart-concordance-samples-container'
                        }`}
                    >
                        <BaseModal
                            isOpen={modalShown}
                            onClose={() => setModalShown(false)}
                            title=""
                            hideHeader={true}
                        >
                            <div className="modal-header">
                                <Container>
                                    <Row>
                                        <Col xs={4}>
                                            <h3>
                                                {`${i18n.t('ch_chart_event.text')} `}
                                                {activeEventId}
                                                {
                                                    userCanAccessEventDetailView ? (
                                                        <Tooltip
                                                            content={i18n.t('jt_event_view.text')}
                                                            placement="top"
                                                        >
                                                            <a
                                                                href={activeEventId ? buildEventDetailURL(activeEventId) : '#'}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                style={{
                                                                    color: '#009efb',
                                                                    textDecoration: 'none'
                                                                }}
                                                            >
                                                                <Tooltip content={i18n.t('jt_view.text')} placement="top">
                                                                    <Button
                                                                        variant="outline-info"
                                                                        size="sm"
                                                                        icon="mdi-eye"
                                                                        data-cy="concordance-wrapper-eye-outlineinfo-btn" />
                                                                </Tooltip>
                                                            </a>
                                                        </Tooltip>
                                                    ) : null
                                                }
                                            </h3>
                                        </Col>
                                        <Col xs={3}>
                                            <h3>
                                                {`${i18n.t('ch_chart_languge.text')} `}
                                                {activeEvent ? (activeEvent.event_lang) : null}
                                            </h3>
                                        </Col>
                                        <Col xs={5}>
                                            <h3>
                                                {
                                                    activeEvent
                                                        ? configLoaded ? formatDateTime(activeEvent.event_datetime, false, {zone: 'utc'},
                                                        ) : '' : null
                                                }
                                            </h3>
                                        </Col>
                                    </Row>
                                    <button
                                        type="button"
                                        className="close position-absolute top-0 end-0 mr-3 mt-3"
                                        aria-label="Close"
                                        onClick={() => setModalShown(false)}
                                    >
                                        <span aria-hidden="true">&times;</span>
                                    </button>
                                </Container>
                            </div>
                            <hr className="my-0" />
                            <div className="modal-body">
                                {modalContents}
                            </div>
                        </BaseModal>

                        <div className="concordance-lines">
                            {data.map((obj) => (
                                <div
                                    className={`concordance-line ${previewClass}`}
                                    key={obj.key_id}
                                    onClick={previewOnly ? undefined : () => openDetailModal(obj)}
                                >
                                    <span className="concordance-line__left">{obj.left_print}</span>
                                    <span className="concordance-line__keyword">{obj.query}</span>
                                    <span className="concordance-line__right">{obj.right_print}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                    {!chartSettingsView ? (
                        <div className="chart-query-time-indicator">
                            <p>
                                {i18n.t('table_chart_query_duration.text', {queryDuration})}
                            </p>
                        </div>
                    ) : null}
                </>
            );
        } else {
            return (
                <CellLoader fullHeight={false} />
            );
        }
    },
);

export default ConcordanceWrapper;
